import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/client";
import {
  type Capture, CaptureError, imageFromClipboard, readCapture, releaseCapture,
} from "./capture";
import { CUSTOM_CSS_KEY } from "../settings/catalog";
import { replaceRules, rulesFor, stringifyRules } from "../settings/css";
import { fromElement, selectorOf } from "../settings/cssSelector";
import { setPreview } from "../settings/cssPreview";
import { useSettings } from "../settings/context";
import { Button } from "../ui/Button";

/**
 * PERSONNALISER L'APPARENCE D'UN BLOC — en le demandant, en français.
 *
 * Le client veut ce titre en majuscules, cette tuile plus ronde, ce bouton moins large.
 * Sans ça, la seule issue est une intervention dans le code, donc un déploiement, pour
 * un seul événement.
 *
 * ⚠️ NI LE MODÈLE NI LE PROMPT NE SONT ÉCRITS ICI. `POST /gpt/generate-css` existe et
 * tourne : elle reçoit le sélecteur, le CSS déjà en place, la consigne et le HTML du
 * bloc, et rend la règle. La V1 s'en sert depuis son `DesignMode`. On lui apporte
 * seulement une prise et une relecture.
 *
 * ⚠️ LE SÉLECTEUR EST LE NOM DU BLOC, PAS UN CHEMIN DANS LE DOM. `[data-cfg="…"]` est
 * déjà un contrat dans ce dépôt ; un sélecteur calculé depuis le DOM encoderait les
 * classes internes de la V2 — on a changé `.form` en flex et déplacé `.fcase` le mois
 * dernier, ce qui aurait cassé la règle d'un client en silence, longtemps après, sans
 * que personne ne fasse le lien.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

async function demanderCss(corps: {
  selector: string;
  currentCss: string;
  instruction: string;
  outerHTML: string;
  /** La capture jointe, en base64 — ce qu'on voit, ou ce qu'on veut. Voir `capture.ts`. */
  image?: { mediaType: string; data: string };
}): Promise<string> {
  const answer = await apiFetch<{ css?: string }>("/gpt/generate-css", {
    api: "apix",
    method: "POST",
    body: corps,
  });
  return (answer?.css ?? "").trim();
}

/**
 * ⚠️ LA MODALE EST PILOTÉE, ET SÉPARÉE DE SON DÉCLENCHEUR. Deux portes y mènent : le
 * bouton de la carte, qui suppose qu'on sache déjà quel bloc survoler, et le sélecteur
 * du panneau, qui répond à « qu'est-ce que je peux personnaliser ? ». La dupliquer
 * aurait fait diverger deux fois le même écran — dont le garde-fou de l'aperçu.
 */
export function AppearanceModal({
  open,
  title,
  selector,
  element,
  onClose,
}: {
  open: boolean;
  /** Comment nommer ce qu'on personnalise, à l'écran. */
  title: string;
  /**
   * ⚠️ LE SÉLECTEUR VIENT DE L'APPELANT, il ne se recalcule pas ici. Deux portes y
   * mènent — le bloc survolé sur la carte, l'élément visé depuis le panneau — et
   * chacune sait mieux que cette modale ce qu'elle a désigné. Le reconstruire à partir
   * d'un seul `target` interdirait précisément ce que le viseur apporte : atteindre un
   * élément DANS le bloc.
   */
  selector: string;
  element: HTMLElement;
  onClose: () => void;
}) {
  const { value, write } = useSettings();
  const dialog = useRef<HTMLDialogElement>(null);

  const [instruction, setInstruction] = useState("");
  const [propose, setPropose] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * LA CAPTURE JOINTE À LA PROCHAINE DEMANDE. « Ce titre-là, comme ça » se montre mieux
   * qu'il ne se décrit : une capture annotée, ou une maquette du résultat voulu, part
   * avec la consigne et le modèle la voit avant de lire. Elle ne sert qu'à CETTE
   * demande — elle est retirée dès que la proposition arrive, et pour affiner il
   * suffit d'en joindre une autre.
   */
  const [capture, setCapture] = useState<Capture | null>(null);
  const [reading, setReading] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const captureRef = useRef<Capture | null>(null);
  captureRef.current = capture;

  const attach = (next: Capture | null) => {
    releaseCapture(captureRef.current);
    setCapture(next);
  };

  const joindre = async (file: File | null) => {
    if (!file) return;
    setReading(true);
    setFailure(null);
    try {
      attach(await readCapture(file));
    } catch (cause) {
      setFailure(
        cause instanceof CaptureError ? cause.message : "La capture n'a pas pu être lue.",
      );
    } finally {
      setReading(false);
    }
  };

  const feuille = typeof value(CUSTOM_CSS_KEY) === "string"
    ? (value(CUSTOM_CSS_KEY) as string)
    : "";

  /* Ce qui vise DÉJÀ cet élément, et rien d'autre : le modèle n'a pas à connaître les
     règles des voisins, et les lui donner l'inviterait à y toucher. Voir `rulesFor`
     pour ce que « viser » englobe. */
  const alreadyRules = rulesFor(feuille, selector);
  const already = stringifyRules(alreadyRules);
  /* Les sélecteurs que l'acceptation RETIRE avant de poser la proposition : exactement
     ceux qu'on a montrés. Une clé stable pour les effets, la liste pour l'aperçu. */
  const replaced = alreadyRules.map((r) => r.selector);
  const replacedKey = replaced.join("\n");
  const alreadyRef = useRef(already);
  alreadyRef.current = already;

  /**
   * ═══ ELLE ROUVRE SUR CE QUI EST EN PLACE ═══
   *
   * ⚠️ C'ÉTAIT LE VRAI MANQUE. Sur un élément déjà personnalisé, la modale s'ouvrait
   * VIDE : la règle en place n'était donnée qu'au modèle, jamais à l'organisateur.
   * Il ne pouvait ni la lire, ni la corriger à la main, ni voir qu'elle existait —
   * et une seconde demande semblait partir de rien, ou ne rien changer. Ouvrir sur
   * la règle en place, éditable, en fait ce que son nom promet : une modification.
   * L'aperçu qui en découle est identique à la feuille enregistrée, donc rien ne
   * bouge à l'écran tant qu'on n'a rien changé.
   */
  useEffect(() => {
    if (open) setPropose(alreadyRef.current !== "" ? alreadyRef.current : null);
  }, [open]);
  const untouched = propose !== null && propose === already;
  /**
   * DE QUOI DEMANDER : un texte, OU une capture. Une capture annotée (un cercle, une
   * flèche, « plus gros » écrit dessus) est déjà une consigne ; exiger du texte en plus
   * obligeait à redire ce que l'image montre. Sans rien du tout, le modèle n'a pas de
   * demande — le bouton reste fermé.
   */
  const askable = instruction.trim() !== "" || capture !== null;

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  /**
   * ⚠️ ON REGARDE LA MODIFICATION AVANT DE L'ACCEPTER. Lire du CSS ne dit pas ce que
   * ça donne : `font-size: 1.2em` se juge sur la page, pas dans un champ de texte.
   * L'aperçu est donc posé à chaque frappe et suit les corrections faites à la main.
   *
   * ⚠️ ET IL SE RETIRE AU DÉMONTAGE, pas seulement à la fermeture. Cette modale vit
   * dans la carte du `SpotEditor`, qui se referme dès que la souris la quitte : sans
   * ce nettoyage, un écart de geste laisserait l'aperçu collé au site jusqu'au
   * rechargement — une règle que personne n'a acceptée, et qui ne figure dans aucune
   * liste d'où la retirer.
   */
  useEffect(() => {
    setPreview(open ? propose : null, replaced);
    // `replacedKey` tient lieu de la liste : même contenu, même effet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propose, replacedKey]);

  useEffect(() => () => {
    setPreview(null);
    releaseCapture(captureRef.current);
  }, []);

  const close = () => {
    setInstruction("");
    setPropose(null);
    setFailure(null);
    attach(null);
    onClose();
  };

  const demander = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const css = await demanderCss({
        selector,
        /* ⚠️ CE QU'ON REGARDE, PAS CE QUI EST ENREGISTRÉ. Reprendre « plus petit » sur
           une proposition en cours doit l'AFFINER : lui donner la règle enregistrée
           ferait repartir le modèle de l'état d'avant, et le bleu qu'on venait
           d'obtenir disparaîtrait à la deuxième demande. */
        currentCss: propose ?? already,
        instruction: instruction.trim(),
        // Le contexte du modèle : ce qu'il y a réellement dans le bloc.
        outerHTML: element.outerHTML.slice(0, 4000),
        ...(capture ? { image: { mediaType: capture.mediaType, data: capture.data } } : {}),
      });
      if (!css) {
        setFailure("Le modèle n'a rien proposé. Reformulez la demande.");
        return;
      }
      setPropose(css);
      setInstruction("");
      /* La capture a servi : la garder ferait repartir chaque « Reprendre » avec la
         même image, y compris pour une consigne qui ne la concerne plus. */
      attach(null);
    } catch (cause) {
      setFailure(
        cause instanceof Error && cause.message
          ? cause.message
          : "La demande n'a pas abouti.",
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * RETIRER, D'ICI. La liste « Apparence sur mesure » du panneau le permettait déjà,
   * mais c'est dans cette modale qu'on regarde une règle et qu'on la juge : la refuser
   * obligeait à la refermer, retrouver la ligne dans le panneau, et cliquer là-bas.
   * Demandé le 2026-09-13. On retire exactement l'ensemble montré (voir `rulesFor`),
   * rien de plus, et on ne pose rien à la place.
   */
  const remove = () => {
    if (replaced.length === 0) return;
    write(CUSTOM_CSS_KEY, replaceRules(feuille, replaced, ""));
    close();
  };

  const apply = () => {
    if (!propose) return;
    /* Ce qui visait l'élément est RETIRÉ, puis la proposition posée : redemander sur
       le même bloc remplace tout ce qu'on a montré, rien ne s'accumule — voir
       `replaceRules`. Écrit AVANT que `close()` ne retire l'aperçu : les deux donnent
       exactement la même feuille, donc rien ne clignote entre les deux rendus. */
    write(CUSTOM_CSS_KEY, replaceRules(feuille, replaced, propose));
    close();
  };

  return (
    <dialog
        ref={dialog}
        className="ask edit appa"
        /**
         * ⚠️ ELLE NE SE FERME PAS AU CLIC DEHORS, ET C'EST INDISPENSABLE ICI. Le fond
         * est transparent pour qu'on VOIE l'aperçu : « dehors », c'est donc la page
         * qu'on est justement en train de regarder. Un clic pour vérifier un détail
         * refermait la modale et emportait la règle proposée — le geste le plus naturel
         * du moment était aussi celui qui détruisait le travail.
         *
         * Échap reste : c'est une touche qu'on ne presse pas par accident, et un
         * dialogue qu'aucun clavier ne peut quitter est un piège.
         */
        onCancel={(e) => {
          e.preventDefault();
          if (!busy) close();
        }}
      >
        <div className="ask__b">
          <div className="edit__h">
            <b>Personnaliser « {title} »</b>
          </div>

          <p className="appa__p">
            La règle ne vaudra que pour <b>cet événement</b>, et seulement sur ce bloc.
            Elle se retire à tout moment, ici ou depuis « Apparence », dans l'onglet
            « Le site » du panneau.
          </p>

          <label className="nvd__l">
            {propose === null
              ? "Ce que vous voulez changer"
              : untouched
                ? "Ce que vous voulez changer, par-dessus la règle en place"
                : "Autre chose à changer ? Reprenez par-dessus"}
            <input
              type="text"
              value={instruction}
              autoFocus
              placeholder={
                capture
                  ? "facultatif : la capture peut suffire"
                  : "mets le titre en bleu et un peu plus petit"
              }
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && askable && !busy) void demander();
              }}
              /* Une capture collée (Ctrl+V) dans le champ de consigne : c'est le geste
                 le plus court après une capture d'écran. Le texte collé suit son cours. */
              onPaste={(e) => {
                const file = imageFromClipboard(e.clipboardData?.items);
                if (file) {
                  e.preventDefault();
                  void joindre(file);
                }
              }}
            />
          </label>

          {/**
           * LA CAPTURE JOINTE. Un bouton et le collage y mènent ; la vignette dit ce qui
           * partira, et la croix le retire. Rien n'est envoyé nulle part avant
           * « Demander » : voir `capture.ts`.
           */}
          <div className="appa__cap">
            <input
              ref={picker}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                void joindre(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
            {capture ? (
              <>
                <img className="appa__cap-img" src={capture.previewUrl} alt="Capture jointe" />
                <span className="appa__cap-t">
                  Capture jointe : elle partira avec la prochaine demande. Si elle dit
                  tout (annotations, texte), le champ peut rester vide.
                </span>
                <button
                  type="button"
                  className="appa__cap-x"
                  disabled={busy}
                  title="Retirer la capture"
                  aria-label="Retirer la capture"
                  onClick={() => attach(null)}
                >
                  ×
                </button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  loading={reading}
                  disabled={busy}
                  onClick={() => picker.current?.click()}
                >
                  Joindre une capture
                </Button>
                <span className="appa__cap-t">
                  ou collez-la (Ctrl+V) dans le champ. Montrez ce qui est là, annoté, ou ce
                  que vous voulez obtenir.
                </span>
              </>
            )}
          </div>

          {/**
           * ⚠️ LE CSS S'AFFICHE AVANT DE S'APPLIQUER, ET RESTE MODIFIABLE. Un modèle
           * peut se tromper ; poser sa réponse sur un événement en production sans que
           * personne ne l'ait lue, c'est confier la mise en page d'un site à un
           * aller-retour qu'on n'a pas relu. Et le champ est éditable parce qu'une
           * réponse à 90 % juste se corrige en dix secondes — la refuser en bloc
           * obligerait à tout recommencer pour une unité près.
           */}
          {propose !== null && (
            <label className="nvd__l">
              {untouched
                ? "Règle en place sur cet élément : modifiez-la ici, ou décrivez le changement au-dessus"
                : "Règle proposée, déjà appliquée à la page derrière : corrigez-la si besoin"}
              <textarea
                className="appa__css"
                rows={6}
                value={propose}
                spellCheck={false}
                onChange={(e) => setPropose(e.target.value)}
              />
            </label>
          )}

          {failure && (
            <p className="fed__err" role="alert">
              {failure}
            </p>
          )}

          {/**
           * ⚠️ « DEMANDER » RESTE APRÈS UNE PROPOSITION, ET NE LA REMPLACE PAS PAR
           * « APPLIQUER ». Une mise en page se cherche : on demande le bleu, on voit,
           * on veut plus petit. Tant qu'il fallait accepter ou renoncer pour reformuler,
           * affiner voulait dire enregistrer une étape intermédiaire sur un événement en
           * production, puis recommencer par-dessus.
           */}
          <div className="ask__a">
            <Button type="button" variant="ghost" disabled={busy} onClick={close}>
              Annuler
            </Button>
            {/* Seulement s'il y a quelque chose à retirer : un bouton qui ne fait rien
                est une promesse vide. */}
            {replaced.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                className="btn--off"
                disabled={busy}
                onClick={remove}
              >
                Retirer la règle
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              loading={busy}
              disabled={!askable}
              onClick={() => void demander()}
            >
              {propose === null ? "Demander" : "Reprendre"}
            </Button>
            {propose !== null && (
              <Button
                type="button"
                /* Rien à appliquer tant que c'est, mot pour mot, la règle en place. */
                disabled={busy || propose.trim() === "" || untouched}
                onClick={apply}
              >
                Appliquer
              </Button>
            )}
          </div>
      </div>
    </dialog>
  );
}

/**
 * LA PORTE DE LA CARTE — sur le bloc qu'on est en train de survoler.
 *
 * L'autre porte est le sélecteur du panneau (`CssRules`) : celle-ci suppose qu'on sache
 * déjà où pointer, l'autre montre ce qui est disponible. Les deux ouvrent la même
 * modale.
 */
export function Appearance({
  title,
  target,
  element,
}: {
  title: string;
  target: string;
  element: HTMLElement;
}) {
  const { saving } = useSettings();
  const [open, setOpen] = useState(false);
  /* Depuis la carte, c'est le bloc ENTIER : elle ne sait pas viser plus fin. Pour un
     élément précis, le viseur du panneau.
     ⚠️ CALCULÉ COMME LE VISEUR, PAS ÉCRIT `[data-cfg="…"]`. Sur une section, le bloc
     seul désigne celui de TOUTES les sections ; `selectorOf` le borne à l'exemplaire
     regardé (#556). */
  const node = fromElement(element);
  const selector = node ? selectorOf(node).selector : `[data-cfg="${target}"]`;

  return (
    <>
      <button
        type="button"
        className="appa__o"
        disabled={!saving}
        title={
          saving
            ? `Décrire ce que vous voulez changer sur « ${title} »`
            : "Indisponible : cet écran n'est relié à aucun événement."
        }
        onClick={() => setOpen(true)}
      >
        ✎ Personnaliser l'apparence
      </button>
      <AppearanceModal
        open={open}
        title={title}
        selector={selector}
        element={element}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
